import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { InsightEntity } from 'modules/insights-v2/types';
import { extractRejectionCategory } from 'modules/insights-v2/utils';
import PurchaseDetailLoaderView from 'modules/purchases/PurchaseDetailLoaderView';

import { RejectedPurchaseItem, RejectedPurchasesTableColumnIDs } from './types';
import InfiniteTable from '../InfiniteTable';
import { SortOrder, SortState } from '../types';

type RejectedPurchasesTableProps = {
  className?: string;
  insightEntity: InsightEntity;
  isFetchingNextPage: boolean;
  isFetchingPreviousPage: boolean;
  items: RejectedPurchaseItem[];
  sort?: SortState<RejectedPurchasesTableColumnIDs>;
  onFetchNextPage?: () => void;
  onFetchPreviousPage?: () => void;
  handleSort?: (sort: SortState<RejectedPurchasesTableColumnIDs>) => void;
};

const TrimmedText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RejectedPurchasesTable = ({
  className,
  insightEntity,
  items,
  isFetchingNextPage,
  isFetchingPreviousPage,
  sort,
  onFetchNextPage,
  onFetchPreviousPage,
  handleSort,
}: RejectedPurchasesTableProps) => {
  const { formatMessage, locale } = useIntl();
  const [selected, setSelected] = React.useState<RejectedPurchaseItem | undefined>(undefined);

  const columnWidths =
    insightEntity.type === 'venue'
      ? {
          VENUE_COLUMN_WIDTH: 0, // not shown
          ORDER_TIME_COLUMN_WIDTH: 20,
          ORDER_NUMBER_COLUMN_WIDTH: 10,
          REASON_COLUMN_WIDTH: 20,
          ORDER_TOTAL_COLUMN_WIDTH: 20,
          COMMENT_COLUMN_WIDTH: 30,
        }
      : {
          VENUE_COLUMN_WIDTH: 20,
          ORDER_TIME_COLUMN_WIDTH: 15,
          ORDER_NUMBER_COLUMN_WIDTH: 10,
          REASON_COLUMN_WIDTH: 20,
          ORDER_TOTAL_COLUMN_WIDTH: 15,
          COMMENT_COLUMN_WIDTH: 20,
        };

  const venue = useMemo(() => {
    if (insightEntity.type === 'venue') {
      return insightEntity.venue;
    }
    if (insightEntity.type === 'merchant') {
      return insightEntity.venues.find(v => v.id === selected?.venueId);
    }

    // @todo navjotahuja92 add support for enterprise, brand
    return undefined;
  }, [insightEntity, selected]);

  const onSelect = (record: RejectedPurchaseItem) => {
    setSelected(record);
  };

  return (
    <>
      <InfiniteTable
        className={className}
        items={items}
        fetchingNextPage={isFetchingNextPage}
        fetchingPreviousPage={isFetchingPreviousPage}
        onFetchNextPage={onFetchNextPage}
        onFetchPreviousPage={onFetchPreviousPage}
        sort={sort}
        selected={selected}
        onSelect={insightEntity.type === 'merchant' || insightEntity.type === 'venue' ? onSelect : undefined}
        columns={[
          ...(insightEntity.type === 'venue'
            ? []
            : [
                {
                  id: RejectedPurchasesTableColumnIDs.VENUE,
                  label: formatMessage({ id: 'insights.rejections.purchases-table.venue' }),
                  width: columnWidths.VENUE_COLUMN_WIDTH,
                  render: (record: RejectedPurchaseItem) => (
                    <TrimmedText title={record.venueName ?? undefined}>{record.venueName}</TrimmedText>
                  ),
                },
              ]),
          {
            id: RejectedPurchasesTableColumnIDs.ORDER_TIME,
            label: formatMessage({ id: 'insights.rejections.purchases-table.order-time' }),
            width: columnWidths.ORDER_TIME_COLUMN_WIDTH,
            render: (record: RejectedPurchaseItem) =>
              DateTime.fromISO(record.timeReceivedLocal).toLocaleString(DateTime.DATETIME_SHORT),
            enableSort: true,
            handleSort: (sortOrder: SortOrder) =>
              handleSort?.({ sortBy: RejectedPurchasesTableColumnIDs.ORDER_TIME, sortOrder }),
          },
          {
            id: RejectedPurchasesTableColumnIDs.ORDER_NUMBER,
            label: formatMessage({ id: 'insights.rejections.purchases-table.order-number' }),
            width: columnWidths.ORDER_NUMBER_COLUMN_WIDTH,
            render: (record: RejectedPurchaseItem) => `#${record.orderNumber}`,
          },
          {
            id: RejectedPurchasesTableColumnIDs.REASON,
            label: formatMessage({ id: 'insights.rejections.purchases-table.reason' }),
            width: columnWidths.REASON_COLUMN_WIDTH,
            render: (record: RejectedPurchaseItem) =>
              formatMessage({ id: extractRejectionCategory({ label: record.rejectionCategory }) }),
          },
          {
            id: RejectedPurchasesTableColumnIDs.ORDER_TOTAL,
            label: formatMessage({ id: 'insights.rejections.purchases-table.order-total' }),
            width: columnWidths.ORDER_TOTAL_COLUMN_WIDTH,
            render: (record: RejectedPurchaseItem) => {
              const vat = record.totalPriceWithVat - record.totalPriceNoVat;
              const formattedVat = vat.toLocaleString(locale, {
                signDisplay: 'always',
                currency: record.currency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
              return (
                <>
                  <MonetaryAmount amount={record.totalPriceNoVat} currency={record.currency} /> {formattedVat}
                </>
              );
            },
          },
          {
            id: RejectedPurchasesTableColumnIDs.COMMENT,
            label: formatMessage({ id: 'insights.rejections.purchases-table.comment' }),
            width: columnWidths.COMMENT_COLUMN_WIDTH,
            render: (record: RejectedPurchaseItem) => (
              <TrimmedText title={record.rejectionMessage ?? undefined}>{record.rejectionMessage}</TrimmedText>
            ),
          },
        ]}
      />
      {/* todo: @navjotahuja92 figure out how to move this into common component to avoid cross module import. */}
      {selected && venue && (
        <PurchaseDetailLoaderView
          onClose={() => setSelected(undefined)}
          selectedPurchaseId={selected.id}
          showDeliveryFees={false}
          shouldDeliveryFeesIncludeServiceFees={false}
          orderComment={selected.rejectionMessage ?? undefined}
          venue={venue}
        />
      )}
    </>
  );
};
export default RejectedPurchasesTable;
